/**
 * Short links for a bulk send: reading the server-sent event stream of
 * `POST /comms/v2/generate_short_links_stream` and describing its result.
 *
 * The route (CommunicationManagementService `routers/comms.py`) writes one
 * `data: <json>` event per step, separated by a blank line:
 *   {"type": "progress", "processed": N, "total": T}   every 100 rows
 *   {"type": "complete", "processed_data": [...rows], "total_rows": T,
 *    "columns": [...], "short_link_columns": [...], "failed_rows": [row indexes]}
 *   {"type": "error", "message": "..."}
 * A failed row keeps its original links.
 */

/**
 * Split a stream buffer into its complete events.
 *
 * @param {string} buffer text read so far
 * @returns {{events: object[], rest: string}} the parsed events, and the
 *     incomplete tail to prepend to the next chunk. A malformed event is
 *     skipped rather than ending the stream.
 */
export const parseSseBuffer = (buffer) => {
    const parts = String(buffer ?? '')
        .replace(/\r\n/g, '\n')
        .split('\n\n')
    const rest = parts.pop() ?? ''
    const events = []
    parts.forEach((part) => {
        const data = part
            .split('\n')
            .filter((line) => line.startsWith('data:'))
            .map((line) => line.slice(5).replace(/^ /, ''))
            .join('\n')
        if (!data) return
        try {
            events.push(JSON.parse(data))
        } catch {
            // Malformed event: skip it, the next one may still parse.
        }
    })
    return { events, rest }
}

/** The page's view of a `complete` event. */
export const shortLinkResultFrom = (event) => ({
    rows: Array.isArray(event?.processed_data) ? event.processed_data : [],
    total: Number(event?.total_rows) || 0,
    columns: Array.isArray(event?.short_link_columns)
        ? event.short_link_columns
        : [],
    failedRows: Array.isArray(event?.failed_rows) ? event.failed_rows : [],
})

const rowsWord = (n) => `${n.toLocaleString()} row${n === 1 ? '' : 's'}`

/**
 * One line saying how the short links went.
 *
 * @param {{total: number, failedRows: number[]}} result
 * @returns {{level: 'success'|'warning'|'error', text: string}}
 */
export const shortLinkSummary = ({ total = 0, failedRows = [] } = {}) => {
    const failed = failedRows.length
    if (failed === 0)
        return {
            level: 'success',
            text: `Short links made for all ${rowsWord(total)}.`,
        }
    if (failed >= total)
        return {
            level: 'error',
            text: `No short links were made: the short link service saved none of the ${rowsWord(
                total
            )}, so the original links are unchanged. Try again.`,
        }
    return {
        level: 'warning',
        text: `Short links made for ${(
            total - failed
        ).toLocaleString()} of ${rowsWord(total)}. ${rowsWord(
            failed
        )} kept the original links. Try again to shorten them.`,
    }
}
